import { useLayoutEffect, useState } from 'react'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowRightIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Spinner } from '@/components/ui/spinner'
import { PhotoStepGallery } from '@/features/experience-shell/components/photo-step-gallery'
import { useChangeStep } from '@/features/experience-shell/hooks/use-change-step.ts'
import {
	type Experience,
	StepKeyName
} from '@/features/experience-shell/types.ts'
import { useExperienceShellPhotoUpload } from '@/features/service-vetting/store/services-photo-upload.ts'
import { useTranslation } from '@/translations/i18n'
import { checkIsItineraryLocked } from '../utils'
import { Content, Footer } from './atoms'

export const PhotosStep = ({
	gallery,
	itineraries,
	isUpgrade
}: {
	gallery: Experience['gallery']
	itineraries: Experience['itineraries']
	isUpgrade: boolean
}) => {
	const changeStep = useChangeStep({
		experienceId: gallery.submissionId,
		currentStep: StepKeyName.enum.photos,
		isUpgrade
	})
	const addGalleryItems = useExperienceShellPhotoUpload(
		(store) => store.addGalleryItems
	)
	const clearGallery = useExperienceShellPhotoUpload(
		(store) => store.clearGallery
	)
	const galleryCounter = useExperienceShellPhotoUpload(
		(store) => store.galleryCounter
	)
	const [isErrorRendered, setIsErrorRendered] = useState(false)
	const { t } = useTranslation()

	useLayoutEffect(() => {
		addGalleryItems(
			gallery.images.map((item) => ({
				id: item.id,
				status: 'uploaded',
				url: item.url,
				thumbnails: item.thumbnails,
				isCover: item.isCover,
				name: item.name
			}))
		)
		return () => clearGallery()
	}, [gallery, addGalleryItems, clearGallery])

	return (
		<>
			<Content title={t('experience_shell:step_photos')}>
				<PhotoStepGallery
					experienceId={gallery.submissionId}
					galleryId={gallery.id}
				/>
				<InputHelpText
					error={isErrorRendered ? t('errors:required') : undefined}
				/>
			</Content>
			<Footer>
				<ButtonLegacy
					disabled={changeStep.isBackLoading}
					onClick={() => {
						if (isUpgrade && galleryCounter === 0) {
							setIsErrorRendered(true)
							return
						}
						changeStep.handleChangeStep({
							isBack: true,
							stepToGoTo: StepKeyName.enum.location
						})
					}}
					type='button'
					variant='tertiary'
				>
					{t('common:back')}
					{changeStep.isBackLoading && <Spinner />}
				</ButtonLegacy>
				<ButtonLegacy
					disabled={changeStep.isContinueLoading}
					onClick={() => {
						if (isUpgrade && galleryCounter === 0) {
							setIsErrorRendered(true)
							return
						}
						changeStep.handleChangeStep({
							stepToGoTo: checkIsItineraryLocked(itineraries)
								? StepKeyName.enum.pricing_discounts
								: StepKeyName.enum.itinerary
						})
					}}
					type='button'
				>
					{t('common:continue')}
					{changeStep.isContinueLoading ? (
						<Spinner />
					) : (
						<ArrowRightIcon />
					)}
				</ButtonLegacy>
			</Footer>
		</>
	)
}
